import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, GraduationCap, BookOpen, CheckCircle2, PlayCircle, Lock, Award, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/card';
import { courseService } from '../services/course.service';
import { useAuth } from '../hooks/useAuth';
import { openAuthModal } from '../lib/authModalEvents';
import { useToast } from '../components/ui/ToastProvider';
import type { CourseDetail as CourseDetailType } from '@edytech/shared/types';

const formatGHS = (cents: number) =>
  cents === 0 ? 'Free' : `GHS ${(cents / 100).toLocaleString()}`;

export const CourseDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  const { data: course, isLoading, error } = useQuery<CourseDetailType>({
    queryKey: ['course', slug],
    queryFn: () => courseService.getCourseBySlug(slug!),
    enabled: !!slug,
  });

  const enrollMutation = useMutation({
    mutationFn: (courseId: number) => courseService.enroll(courseId),
    onSuccess: () => {
      showToast('Enrolled successfully! Start learning now.', 'success');
      queryClient.invalidateQueries({ queryKey: ['course', slug] });
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
    onError: (err: Error) => showToast(err.message || 'Enrollment failed', 'error'),
  });

  const toggleSection = (id: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return <div className="py-20 text-center text-[rgb(var(--light-secondary))]">Loading course...</div>;
  }

  if (error || !course) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-2xl font-bold">Course not found</h1>
        <Link to="/courses" className="mt-4 inline-block font-medium text-primary hover:underline">
          Back to courses
        </Link>
      </div>
    );
  }

  const enrolled = !!course.enrollment;
  const isComplete = course.enrollment?.status === 'COMPLETED';
  const resumeLessonId = course.enrollment?.lastAccessedLessonId;
  const firstLesson = course.syllabus[0]?.lessons[0];
  const deliverableLessonId = resumeLessonId ?? firstLesson?.id;

  const handleEnroll = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }
    enrollMutation.mutate(course.id);
  };

  const goToLesson = (lessonId: number | null | undefined) => {
    if (!lessonId) return;
    navigate(`/learn/${course.slug}/lessons/${lessonId}`);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mx-auto max-w-4xl">
        <Link to="/courses" className="text-sm font-medium text-primary hover:underline">← Back to courses</Link>

        <div className="mt-4 flex flex-col gap-8 md:flex-row">
          {/* Main content */}
          <div className="flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-primary">{course.category || course.level}</p>
            <h1 className="mt-2 text-3xl font-bold">{course.title}</h1>
            {course.subtitle && <p className="mt-2 text-lg text-[rgb(var(--light-secondary))]">{course.subtitle}</p>}
            <p className="mt-4 text-[rgb(var(--light-secondary))]">{course.description}</p>

            <div className="mt-6 flex flex-wrap gap-6">
              <div className="flex items-center space-x-2 text-sm">
                <Clock className="h-4 w-4 text-primary" />
                <span>{course.durationMinutes} min total</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <BookOpen className="h-4 w-4 text-primary" />
                <span>{course.lessonCount} lessons</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <GraduationCap className="h-4 w-4 text-primary" />
                <span>Certificate with QR verification</span>
              </div>
              {course.instructorName && (
                <span className="text-sm text-[rgb(var(--light-secondary))]">Instructor: {course.instructorName}</span>
              )}
            </div>

            {course.objectives && course.objectives.length > 0 && (
              <div className="mt-8 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-6">
                <h2 className="text-lg font-semibold">What you'll learn</h2>
                <ul className="mt-4 grid gap-2 text-sm text-[rgb(var(--light-secondary))] sm:grid-cols-2">
                  {course.objectives.map((obj, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>
            )}


            {/* Syllabus */}
            <div className="mt-8">
              <h2 className="mb-4 text-lg font-semibold">Course Content</h2>
              {course.syllabus.map((section) => {
                const isExpanded = expandedSections.has(section.id);
                const totalSec = section.lessons.reduce((s, l) => s + l.durationSeconds, 0);
                return (
                  <div key={section.id} className="mb-3 overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))]">
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex w-full items-center justify-between px-5 py-4 text-left"
                    >
                      <div>
                        <p className="font-semibold">{section.title}</p>
                        <p className="text-xs text-[rgb(var(--light-secondary))]">
                          {section.lessons.length} lessons · {Math.round(totalSec / 60)} min
                        </p>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-[rgb(var(--light-secondary))] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <ul className="border-t border-[rgb(var(--border))]">
                        {section.lessons.map((lesson) => {
                          const locked = !enrolled && !lesson.isFree && !lesson.isPreview;
                          return (
                            <li key={lesson.id}>
                              <button
                                onClick={() => {
                                  if (locked) {
                                    handleEnroll();
                                  } else {
                                    goToLesson(lesson.id);
                                  }
                                }}
                                className="flex w-full items-center gap-3 px-5 py-3 text-sm transition-colors hover:bg-[rgb(var(--border))]"
                              >
                                {locked ? (
                                  <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--light-secondary))]" />
                                ) : (
                                  <PlayCircle className="h-4 w-4 shrink-0 text-primary" />
                                )}
                                <span className="flex-1 text-left">{lesson.title}</span>
                                <span className="text-xs text-[rgb(var(--light-secondary))]">{Math.round(lesson.durationSeconds / 60)} min</span>
                                {lesson.isFree && <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Free</span>}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Enrollment card */}
          <div className="md:w-80">
            <Card className="sticky top-24">
              <div className="mb-4 text-center">
                <p className="text-3xl font-bold">{formatGHS(course.price)}</p>
              </div>
              {enrolled ? (
                <div className="space-y-3">
                  {isComplete ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <Award className="h-5 w-5" /> Completed
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[rgb(var(--light-secondary))]">Progress</span>
                        <span className="font-medium">{course.enrollment?.progressPercent}%</span>
                      </div>
                      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-[rgb(var(--border))]">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${course.enrollment?.progressPercent}%` }} />
                      </div>
                    </div>
                  )}
                  <Button className="w-full" onClick={() => goToLesson(deliverableLessonId)}>
                    {isComplete ? 'Review Course' : 'Continue Learning'}
                  </Button>
                  {course.quiz && (
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => navigate(`/learn/${course.slug}/quiz`)}
                    >
                      Take Quiz
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <Button className="w-full" onClick={handleEnroll} disabled={enrollMutation.isPending}>
                    {enrollMutation.isPending ? 'Enrolling...' : 'Enroll Now'}
                  </Button>
                  {course.quiz && (
                    <p className="text-center text-xs text-[rgb(var(--light-secondary))]">
                      Pass the quiz to earn your certificate
                    </p>
                  )}
                </div>
              )}
              <div className="mt-4 border-t border-[rgb(var(--border))] pt-4 text-xs text-[rgb(var(--light-secondary))]">
                <p className="mb-1">✔ Lifetime access</p>
                <p className="mb-1">✔ Certificate with QR verification</p>
                <p>✔ Progress tracked automatically</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
